import React, { useState } from 'react';
import { Upload, Camera, DollarSign, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { poolTypes, poolSizes, poolFeatures } from '../data/poolAnalysisMock';
import { uploadPoolPhotos, calculatePoolCost, submitPoolAnalysis } from '../services/api';

const PoolAnalysis = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Store actual File objects
  const [photoUrls, setPhotoUrls] = useState([]); // Store backend URLs
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    poolType: '',
    poolSize: '',
    features: [],
    address: '',
    timeline: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setUploadedImages([...uploadedImages, ...imageUrls]);
    setUploadedFiles([...uploadedFiles, ...files]); // Store actual files for upload
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFeatureToggle = (featureId) => {
    const features = formData.features.includes(featureId)
      ? formData.features.filter(f => f !== featureId)
      : [...formData.features, featureId];
    setFormData({ ...formData, features });
  };

  const generateEstimate = async () => {
    if (!formData.poolType || !formData.poolSize) {
      toast({
        title: "Missing Information",
        description: "Please select pool type and size to continue.",
        variant: "destructive"
      });
      return;
    }

    setIsCalculating(true);

    try {
      // Upload photos first if any
      if (uploadedFiles.length > 0) {
        const uploadResult = await uploadPoolPhotos(uploadedFiles);
        if (uploadResult.success) {
          setPhotoUrls(uploadResult.files);
        } else {
          toast({
            title: "Upload Warning",
            description: "Photos upload failed, but continuing with estimate.",
            variant: "destructive"
          });
        }
      }

      // Calculate cost from backend
      const result = await calculatePoolCost(formData.poolType, formData.poolSize, formData.features);
      
      if (result.success) {
        setEstimate(result.estimate);
        setStep(3);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to calculate estimate. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in your contact information.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitPoolAnalysis(formData, photoUrls);

      if (result.success) {
        toast({
          title: "Success!",
          description: "Your pool analysis has been submitted! We'll contact you soon with a detailed quote."
        });
        
        // Reset after 3 seconds
        setTimeout(() => {
          setStep(1);
          setUploadedImages([]);
          setUploadedFiles([]);
          setPhotoUrls([]);
          setFormData({
            name: '',
            email: '',
            phone: '',
            poolType: '',
            poolSize: '',
            features: [],
            address: '',
            timeline: ''
          });
          setEstimate(null);
        }, 3000);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to submit analysis. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="pool-analysis" className="py-20 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-cyan-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full mb-4">
            <span className="text-cyan-300 font-semibold text-sm">Free Pool Analysis</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get Your Instant Pool Cost Estimate
          </h2>
          <p className="text-lg text-gray-300">
            Upload photos of your backyard and answer a few questions to receive a detailed cost breakdown in minutes!
          </p>
        </div>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: 'Upload Photos' },
              { num: 2, label: 'Pool Details' },
              { num: 3, label: 'Get Estimate' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                    step >= s.num 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white scale-110 shadow-lg' 
                      : 'bg-white/10 text-gray-400'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-6 h-6" /> : s.num}
                  </div>
                  <span className={`hidden md:block font-medium ${step >= s.num ? 'text-cyan-400' : 'text-gray-500'}`}>
                    {s.label}
                  </span>
                </div>
                {idx < 2 && (
                  <div className={`w-16 h-1 rounded transition-all duration-300 ${
                    step > s.num ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-white/10'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Upload Photos */}
        {step === 1 && (
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Camera className="w-6 h-6 text-cyan-400" />
                  Upload Your Backyard Photos
                </CardTitle>
                <p className="text-gray-400 mt-2">
                  Share photos from different angles to help us understand your space better
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Upload Area */}
                  <div className="border-2 border-dashed border-cyan-400/50 rounded-xl p-12 text-center hover:border-cyan-400 transition-all duration-300 bg-white/5 cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="cursor-pointer">
                      <Upload className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-white mb-2">Click to Upload Photos</h3>
                      <p className="text-gray-400">or drag and drop your images here</p>
                      <p className="text-sm text-gray-500 mt-2">PNG, JPG up to 10MB each</p>
                    </label>
                  </div>

                  {/* Uploaded Images Preview */}
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {uploadedImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img}
                            alt={`Upload ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-cyan-400/50"
                          />
                          <button
                            onClick={() => setUploadedImages(uploadedImages.filter((_, i) => i !== idx))}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    onClick={() => setStep(2)}
                    disabled={uploadedImages.length === 0}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-lg py-6"
                  >
                    Continue to Pool Details
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Pool Details */}
        {step === 2 && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Pool Type */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-xl text-white">Select Pool Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {poolTypes.map(type => (
                    <div
                      key={type.id}
                      onClick={() => setFormData({ ...formData, poolType: type.id })}
                      className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        formData.poolType === type.id
                          ? 'border-cyan-400 bg-cyan-500/20'
                          : 'border-white/20 bg-white/5 hover:border-cyan-400/50'
                      }`}
                    >
                      <h3 className="text-lg font-bold text-white mb-2">{type.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{type.description}</p>
                      <p className="text-cyan-400 font-bold">From ${type.basePrice.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pool Size */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-xl text-white">Choose Pool Size</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {poolSizes.map(size => (
                    <div
                      key={size.id}
                      onClick={() => setFormData({ ...formData, poolSize: size.id })}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 text-center ${
                        formData.poolSize === size.id
                          ? 'border-cyan-400 bg-cyan-500/20'
                          : 'border-white/20 bg-white/5 hover:border-cyan-400/50'
                      }`}
                    >
                      <p className="text-white font-bold">{size.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-xl text-white">Add Features (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {poolFeatures.map(feature => (
                    <div
                      key={feature.id}
                      onClick={() => handleFeatureToggle(feature.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 flex items-center justify-between ${
                        formData.features.includes(feature.id)
                          ? 'border-cyan-400 bg-cyan-500/20'
                          : 'border-white/20 bg-white/5 hover:border-cyan-400/50'
                      }`}
                    >
                      <div>
                        <p className="text-white font-semibold">{feature.name}</p>
                        <p className="text-gray-400 text-sm">+${feature.price.toLocaleString()}</p>
                      </div>
                      {formData.features.includes(feature.id) && (
                        <CheckCircle className="w-6 h-6 text-cyan-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-xl text-white">Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Property Address
                    </label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter your address"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Desired Timeline
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full px-3 py-2 rounded-md bg-white/10 border-2 border-white/20 text-white focus:border-cyan-400 focus:outline-none"
                    >
                      <option value="">Select timeline</option>
                      <option value="immediate">ASAP</option>
                      <option value="1-3months">1-3 months</option>
                      <option value="3-6months">3-6 months</option>
                      <option value="6-12months">6-12 months</option>
                      <option value="planning">Just planning</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 border-2 border-white/20 text-white hover:border-cyan-400 bg-transparent"
              >
                Back
              </Button>
              <Button
                onClick={generateEstimate}
                disabled={isCalculating}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-lg py-6"
              >
                {isCalculating ? 'Calculating...' : 'Generate Estimate'}
                <TrendingUp className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Estimate Results */}
        {step === 3 && estimate && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Estimate Breakdown */}
            <Card className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 backdrop-blur-sm border-2 border-cyan-400/50">
              <CardHeader>
                <CardTitle className="text-3xl text-white flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-cyan-400" />
                  Your Pool Cost Estimate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/20">
                    <span className="text-gray-300">Base Pool Cost</span>
                    <span className="text-white font-bold text-lg">${estimate.base_price.toLocaleString()}</span>
                  </div>
                  {estimate.features > 0 && (
                    <div className="flex justify-between items-center py-3 border-b border-white/20">
                      <span className="text-gray-300">Additional Features</span>
                      <span className="text-white font-bold text-lg">+${estimate.features.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b border-white/20">
                    <span className="text-gray-300">Labor & Installation</span>
                    <span className="text-white font-bold text-lg">${estimate.labor.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/20">
                    <span className="text-gray-300">Permits & Fees</span>
                    <span className="text-white font-bold text-lg">${estimate.permits.toLocaleString()}</span>
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-between items-center py-6 bg-white/10 rounded-xl px-6 mt-4">
                    <span className="text-white text-xl font-bold">Estimated Total</span>
                    <span className="text-cyan-400 font-bold text-3xl">${estimate.total.toLocaleString()}</span>
                  </div>

                  {/* Financing */}
                  <div className="bg-orange-500/20 border-2 border-orange-400/50 rounded-xl p-6">
                    <p className="text-white font-semibold mb-2">💰 Financing Available</p>
                    <p className="text-gray-300">
                      Starting at <span className="text-orange-400 font-bold text-xl">${estimate.monthly_payment}/month</span> with approved credit
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card className="bg-white/5 backdrop-blur-sm border-2 border-white/10">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Get Your Detailed Quote</CardTitle>
                <p className="text-gray-400 mt-2">
                  Enter your contact information and we'll send you a detailed breakdown
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(254) 214-8897"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div className="flex gap-4 mt-6">
                    <Button
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-2 border-white/20 text-white hover:border-cyan-400 bg-transparent"
                    >
                      Back to Edit
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg py-6"
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Detailed Quote'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-gray-400 text-sm">
              * This is an estimated cost. Final pricing may vary based on site conditions and specific requirements.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PoolAnalysis;
